import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, from, lastValueFrom, switchMap, tap } from 'rxjs';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { UserService } from './user.service';
import { LocalStorageService } from './local-storage.service';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { ErrorHandlerService } from './error-handler.service';
import { BaseService } from './base/base.service';
import { MemberService } from './member.service';
import { SpinnerService } from './spinner.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends BaseService {
  private loggedIn = new BehaviorSubject<boolean>(this.hasToken());
  isLoggedIn = this.loggedIn.asObservable();
  private dependent = new BehaviorSubject<boolean>(this.hasDependent());
  isDependent = this.dependent.asObservable();

  readonly isCeara: boolean =
    window.location.href.toLowerCase().includes('ceara') ||
    window.location.href.toLowerCase().includes('sociovozao');

  readonly isDev: boolean =
    window.location.href.toLowerCase().includes('sociotorcedordev') ||
    window.location.href.toLowerCase().includes('localhost');

  readonly useFanBase: boolean = this.isCeara;

  private env = environment;
  roles: Array<string> = [];

  constructor(
    http: HttpClient,
    errorHandlerService: ErrorHandlerService,
    private router: Router,
    private userService: UserService,
    private memberService: MemberService,
    private localStorageService: LocalStorageService,
    private spinnerService: SpinnerService
  ) {
    super(http, errorHandlerService);

    if (new URLSearchParams(window.location.search).get('backoffice') === 'true') {
      this.clearSession();
    }
  }

  public hasToken(): boolean {
    return !!localStorage.getItem('token');
  }

  public hasDependent(): boolean {
    return !!localStorage.getItem('isDependent');
  }

  login(
    username: string,
    password: string,
    returnUrl: string | null = null,
    registerLogin: boolean = false
  ): Observable<LoginAuthModel> {
    let params = new HttpParams();
    params = params.append('showError', 'false');
    if (registerLogin)
      params = params.append('registerLogin', 'true');
    return this.post<LoginAuthModel>(
      `${this.env.authApiUrl}/auth/login`,
      {
        login: username,
        password: password,
        grantType: 'password',
        modulesId: [
          '12472e7c-4abc-11e9-a4d1-0242ac110003',
          '07498bb5-d7b4-4a4d-82f8-5eb9e2497478',
          'e380f282-0015-40a9-aa06-e20b1f291d53',
        ],
      },
      { params }
    ).pipe(
      switchMap((auth) => {
        if (auth && Object.keys(auth).length) {
          return from(this.allowAccessWebsite(auth, returnUrl, registerLogin)).pipe(
            switchMap(() => [auth])
          );
        }
        return [auth];
      })
    );
  }

  loginMemberByBackoffice(userId: string): Observable<LoginAuthModel> {
    return this.http.get<LoginAuthModel>(`${this.env.publicApiAuthUrl}v1/auth/auth/login/${userId}`)
      .pipe(
        switchMap((auth) => {
          if (auth && Object.keys(auth).length) {
            return from(this.allowAccessWebsite(auth, '/dashboard')).pipe(
              switchMap(() => [auth])
            );
          }
          return [auth];
        })
      );
  }

  loginDependent(body: any): Observable<any> {
    return this.http.post<any>(`${this.env.publicApiSocioUrl}v1/socio/memberDependents/login-dependent`, body).pipe(
      tap((auth) => {
        if (auth && Object.keys(auth).length) {
          this.allowAccessDependent(auth);
        }
      })
    );;
  }

  sendResetCodeDependent(email: string): Observable<any> {
    return this.post(
      `${this.env.publicApiSocioUrl}v1/socio/memberDependents/send-reset-code?email=${email}`, {}
    );
  }

  validateResetCode(email: string, code: string): Observable<any> {
    return this.post(
      `${this.env.publicApiSocioUrl}v1/socio/memberDependents/verify-reset-code?email=${email}&code=${code}`, {}
    );
  }

  changeDependentPassword(body: any): Observable<any> {
    return this.put(
      `${this.env.publicApiSocioUrl}v1/socio/memberDependents/reset-password`, body
    );
  }

  private async allowAccessWebsite(auth: any, returnUrl: string | null, registerLogin: boolean = false) {
    this.localStorageService.setItem('token', auth.accessToken);
    this.spinnerService.show();

    try {
      await lastValueFrom(this.memberService.getMemberPublicInfo());

      const loggedInUser = this.userService.currentUserValue;
      let navigateHandled = false;
      if (loggedInUser?.isDependent) {
        await this.allowAccessDependent(loggedInUser);
        navigateHandled = true;
      } else {
        localStorage.removeItem('isDependent');
        localStorage.removeItem('dependenteId');
        this.dependent.next(false);
      }

      if (!registerLogin) {
        this.localStorageService.removeItem('registerLogin');
        this.loggedIn.next(true);
      } else {
        this.localStorageService.setItem('registerLogin', 'true');
      }

      if (!navigateHandled && returnUrl !== null && returnUrl.length >= 0) {
        await this.router.navigate([returnUrl]);
      }
    } catch (error: any) {
      this.localStorageService.removeItem('token');
      this.loggedIn.next(false);

      this.router.navigate(['/login']);
      throw error;
    } finally {
      this.spinnerService.hide();
    }
  }

  private async allowAccessDependent(auth: any) {
    this.dependent.next(true);

    localStorage.setItem('isDependent', 'true');
    localStorage.setItem('dependenteId', auth.memberDependentId);

    // this.localStorageService.setItem('currentDependente', auth);

    // const loginDate = new Date().toISOString().split('T')[0];
    // localStorage.setItem('dependentLoginDate', loginDate);

    await this.router.navigate(['/dashboard-dependente']);
  }

  public getToken(): string {
    return this.localStorageService.getItem('token');
  }

  clearSession() {
    this.loggedIn.next(false);
    this.dependent.next(false);

    localStorage.removeItem('currentDependente');
    localStorage.removeItem('dependenteId');
    localStorage.removeItem('isDependent');
    localStorage.removeItem('dependentLoginDate');

    this.userService.removeCurrentUser();
    this.userService.setCurrentUserSubject(null);
    this.localStorageService.removeItem('token');
    this.localStorageService.removeItem('registerLogin');
    localStorage.removeItem('isSecondaryPlan');
    localStorage.removeItem('secondaryPlanDetail');
  }

  logout(returnUrl = '') {
    this.clearSession();

    const url = this.isDev ? '' : '/login';
    this.router.navigate([url], { queryParams: { returnUrl: returnUrl } })
      .then(() => {
        setTimeout(() => {
          location.reload();
        }, 200);
      });
  }

  isTokenExpired(token: string): boolean {
    const decodedToken = decodeJwt(token);
    const currentTime = Math.floor(Date.now() / 1000);
    return decodedToken.exp <= currentTime;
  }

  recoverPassword(email: string): Observable<any> {
    return this.get(
      `${this.env.authApiUrl}/auth/user-request/recover-password-club?email=${email}`
    );
  }

  saveNewPassword(passwordObj: any, token: string): Observable<any> {
    return this.put(
      `${this.env.authApiUrl}/auth/user-request/password?requestToken=${token}`,
      passwordObj
    );
  }

  validateToken(token: string): Observable<any> {
    return this.put(
      `${this.env.authApiUrl}/auth/user-request/active-account?requestToken=${token}`,
      {}
    );
  }

  changePasswordLoggedIn(
    newPassword: string,
    passwordConfirmation: string,
    authToken: string
  ): Observable<void> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    });

    const body = {
      newPassword,
      passwordConfirmation,
    };

    return this.http.put<void>(`${this.env.authApiUrl}/users/password`, body, {
      headers,
    });
  }

  //criar get e set de roles
  setRoles(roles: Array<string>) {
    this.roles = roles;
  }

  getRoles() {
    return this.roles;
  }
}

function decodeJwt(token: string) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  );
  return JSON.parse(jsonPayload);
}
export interface LoginAuthModel {
  accessExpiresIn: number;
  accessToken: string;
  idSoccerTeam: string;
  login: string;
  name: string;
  refreshExpiresIn: number;
  refreshToken: string;
  roles: Array<string>;
  userId: string;
}
